import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { PageHeader } from '../../components/ui/PageHeader';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { executionTriggerLabel, TriggerTypeBadge } from '../../components/ui/TriggerTypeBadge';
import { canSeeAiCost, formatEur, formatTokens } from '../../lib/ai-cost';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';

interface ExecutionDetail {
  id: string;
  externalExecutionId: string;
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'PARTIAL';
  trigger: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  volume: number | null;
  /** Présents uniquement pour le super-administrateur. */
  aiCostEur?: string | null;
  aiTokens?: number | null;
  resultSummary: string | null;
  errorMessage: string | null;
  errorStep: string | null;
  inputPreview: Record<string, unknown> | null;
  automation: { id: string; name: string; triggerType: string };
}

async function fetchExecution(id: string): Promise<ExecutionDetail> {
  const res = await apiClient.get<ExecutionDetail>(`/executions/${id}`);
  return res.data;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-900">{value ?? '—'}</div>
    </div>
  );
}

export function ExecutionDetailPage() {
  const { user } = useAuth();
  // Coût IA remonté par n8n : super-administrateur uniquement.
  const showAiCost = canSeeAiCost(user?.role);
  const { id } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ['execution', id],
    queryFn: () => fetchExecution(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.status === 'RUNNING' ? 3000 : false),
  });

  if (query.isLoading) {
    return <div className="p-8 text-sm text-slate-500">Chargement…</div>;
  }
  if (!query.data) {
    return <div className="p-8 text-sm text-slate-500">Introuvable.</div>;
  }
  const exec = query.data;

  return (
    <>
      <PageHeader
        helpTopic="executions"
        title={exec.automation.name}
        subtitle={`Exécution ${exec.externalExecutionId}`}
        actions={<StatusBadge status={exec.status} />}
      />
      <section className="grid grid-cols-1 gap-6 px-4 sm:px-6 lg:px-8 py-6 lg:grid-cols-2">
        <div className="surface p-5">
          <h2 className="mb-4 text-sm font-medium text-slate-700">Cycle de vie</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Déclenchée" value={executionTriggerLabel(exec.trigger)} />
            <Field label="Démarrée" value={new Date(exec.startedAt).toLocaleString('fr-FR')} />
            <Field
              label="Terminée"
              value={exec.endedAt ? new Date(exec.endedAt).toLocaleString('fr-FR') : '—'}
            />
            <Field
              label="Durée"
              value={exec.durationMs != null ? `${(exec.durationMs / 1000).toFixed(1)} s` : '—'}
            />
          </div>
        </div>

        <div className="surface p-5">
          <h2 className="mb-4 text-sm font-medium text-slate-700">Métriques</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Volume" value={exec.volume ?? '—'} />
            {showAiCost && (
              <>
                <Field label="Coût IA" value={formatEur(exec.aiCostEur, 4)} />
                <Field label="Jetons IA" value={formatTokens(exec.aiTokens)} />
              </>
            )}
            <Field label="Type" value={<TriggerTypeBadge type={exec.automation.triggerType} />} />
          </div>
        </div>

        {exec.resultSummary && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 lg:col-span-2">
            <h2 className="mb-2 text-sm font-medium text-emerald-900">Résultat</h2>
            <p className="text-sm text-emerald-900">{exec.resultSummary}</p>
          </div>
        )}

        {exec.errorMessage && (
          <div
            data-help="execution-error"
            className="rounded-xl border border-red-200 bg-red-50 p-5 lg:col-span-2"
          >
            <h2 className="mb-2 text-sm font-medium text-red-900">Erreur</h2>
            <p className="text-sm text-red-900">{exec.errorMessage}</p>
            {exec.errorStep && (
              <p className="mt-1 text-xs text-red-700">Étape : {exec.errorStep}</p>
            )}
          </div>
        )}

        {exec.inputPreview && Object.keys(exec.inputPreview).length > 0 && (
          <div className="surface p-5 lg:col-span-2">
            <h2 className="mb-2 text-sm font-medium text-slate-700">Aperçu des entrées</h2>
            <pre className="overflow-x-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
              {JSON.stringify(exec.inputPreview, null, 2)}
            </pre>
            <p className="mt-2 text-xs text-slate-400">
              Aperçu minimal — pas de données métier complètes (RGPD).
            </p>
          </div>
        )}
      </section>
    </>
  );
}
